import { useState, useEffect, useCallback } from 'react';
import { Layout } from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import {
  Users, IndianRupee, TrendingUp, Sparkles, Quote, ArrowUpRight, ArrowDownRight,
  Home, Target, Clock, CheckCircle, RefreshCw, Calendar, Phone, AlertTriangle,
  Eye, Plus, FileText, CreditCard, Building2, MessageSquare, UserCheck,
  ChevronRight, Activity, BarChart3, Wallet, CalendarClock
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '@/utils/api';
import { toast } from 'sonner';

const BRAND = { navy: '#0B1120', gold: '#C9A84C', goldLight: '#D4B366' };

const hindiQuotes = [
  "संपत्ति में निवेश करना अपने भविष्य में निवेश करना है। आज का छोटा कदम कल की बड़ी सफलता बन सकता है।",
  "सफल रियल एस्टेट व्यवसाय का रहस्य है - ग्राहक की संतुष्टि और भरोसा।",
  "हर संपत्ति एक नई कहानी है, हर ग्राहक एक नया अवसर।",
  "रियल एस्टेट में सफलता का मंत्र - ईमानदारी, मेहनत और सही समय पर सही निर्णय।",
  "जमीन और मकान सिर्फ संपत्ति नहीं, किसी के सपनों का घर है।",
  "आज की हर डील कल के 10 रेफरल बन सकती है।",
  "बाजार में उतार-चढ़ाव आते रहेंगे, लेकिन अच्छी सेवा हमेशा याद रखी जाती है।"
];

function formatCurrency(value) {
  if (!value || value === 0) return '₹0';
  return `₹${Number(value).toLocaleString('en-IN')}`;
}

function formatDate(d) {
  if (!d) return '';
  try { return new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }); } catch { return d; }
}

function isOverdue(dateStr) {
  if (!dateStr) return false;
  return new Date(dateStr) < new Date(new Date().toDateString());
}

export default function AdminDashboard({ user, onLogout }) {
  const [stats, setStats] = useState(null);
  const [aiInsight, setAiInsight] = useState('');
  const [loadingInsight, setLoadingInsight] = useState(false);
  const [recentBookings, setRecentBookings] = useState([]);
  const [followupLeads, setFollowupLeads] = useState([]);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [refreshing, setRefreshing] = useState(false);
  const navigate = useNavigate();

  const dailyQuote = hindiQuotes[Math.floor((new Date() - new Date(new Date().getFullYear(), 0, 0)) / 86400000) % hindiQuotes.length];

  const fetchAll = useCallback(async () => {
    try {
      const [statsRes, bookingsRes, leadsRes] = await Promise.allSettled([
        api.get('/dashboard/stats'),
        api.get('/bookings?limit=5'),
        api.get('/leads/followups/today')
      ]);
      if (statsRes.status === 'fulfilled') {
        setStats(statsRes.value.data);
      } else {
        console.error('Dashboard stats fetch failed:', statsRes.reason?.message || statsRes.reason);
      }
      if (bookingsRes.status === 'fulfilled') {
        const data = bookingsRes.value.data;
        setRecentBookings(Array.isArray(data) ? data.slice(0, 5) : []);
      }
      if (leadsRes.status === 'fulfilled') setFollowupLeads(leadsRes.value.data || []);
    } catch (err) {
      console.error('Dashboard fetchAll error:', err);
    }
  }, []);

  useEffect(() => {
    fetchAll();
    const fetchInsight = async () => {
      setLoadingInsight(true);
      try {
        const res = await api.post('/ai/insight', { prompt: 'Provide a brief business insight for today based on current metrics. Keep it under 50 words.' });
        setAiInsight(res.data.insight);
      } catch { setAiInsight('Business insights loading...'); }
      finally { setLoadingInsight(false); }
    };
    fetchInsight();
    const timer = setInterval(() => setCurrentTime(new Date()), 60000);
    return () => clearInterval(timer);
  }, [fetchAll]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      if ('caches' in window) { const cn = await caches.keys(); await Promise.all(cn.map(c => caches.delete(c))); }
      await fetchAll();
    } catch {} finally { setRefreshing(false); }
  };

  const getGreeting = () => {
    const h = currentTime.getHours();
    if (h < 12) return 'Good Morning';
    if (h < 17) return 'Good Afternoon';
    return 'Good Evening';
  };

  const revenueData = stats?.monthly_revenue
    ? Object.entries(stats.monthly_revenue)
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([month, amount]) => ({
          month: new Date(month + '-01').toLocaleDateString('en-US', { month: 'short' }),
          revenue: amount
        }))
    : [];

  const collectionRate = stats?.total_revenue > 0 ? Math.round((stats.total_paid / stats.total_revenue) * 100) : 0;

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-5 max-w-7xl mx-auto" data-testid="admin-dashboard">

        {/* ===== HERO WELCOME ===== */}
        <div className="relative overflow-hidden rounded-2xl" style={{ background: `linear-gradient(135deg, ${BRAND.navy} 0%, #162032 50%, #1a2840 100%)` }}>
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full opacity-5" style={{ background: BRAND.gold }}></div>
            <div className="absolute -bottom-12 -left-12 w-48 h-48 rounded-full opacity-5" style={{ background: BRAND.gold }}></div>
            <div className="absolute top-1/2 right-1/3 w-1 h-1 rounded-full opacity-20" style={{ background: BRAND.gold, boxShadow: `0 0 60px 30px ${BRAND.gold}20` }}></div>
          </div>
          <div className="relative z-10 p-6 md:p-8 flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <div className="flex items-center gap-3 mb-1">
                <p className="text-sm font-medium" style={{ color: BRAND.goldLight }}>
                  {currentTime.toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                </p>
                <button onClick={handleRefresh} disabled={refreshing} className="p-1.5 rounded-lg transition-colors hover:bg-white/10" data-testid="refresh-cache-btn" style={{ color: BRAND.gold }}>
                  <RefreshCw size={14} className={refreshing ? 'animate-spin' : ''} />
                </button>
              </div>
              <h1 className="text-2xl md:text-3xl font-bold text-white" data-testid="welcome-message">
                {getGreeting()}, <span style={{ color: BRAND.gold }}>{user?.full_name?.split(' ')[0] || 'User'}</span>
              </h1>
              <p className="text-slate-400 mt-1 text-sm max-w-lg">Aapke business ka aaj ka overview.</p>
            </div>
            <div className="flex items-center gap-4">
              <div className="hidden md:block bg-white/5 backdrop-blur-sm rounded-xl p-3 border border-white/10">
                <img src="https://customer-assets.emergentagent.com/job_proptech-admin-suite/artifacts/jcoqxxo3_logo%20%281%29.png" alt="Ganesa" className="w-14 h-14 object-contain" />
              </div>
            </div>
          </div>
        </div>

        {/* ===== KEY METRICS ===== */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 md:gap-4">
          {[
            { title: 'Total Revenue', value: formatCurrency(stats?.total_revenue || 0), icon: IndianRupee, accent: '#059669', bg: '#f0fdf4', border: '#bbf7d0' },
            { title: 'Total Bookings', value: stats?.total_bookings || 0, icon: FileText, accent: '#0891b2', bg: '#ecfeff', border: '#a5f3fc' },
            { title: 'Customers', value: stats?.total_customers || 0, icon: Users, accent: '#2563eb', bg: '#eff6ff', border: '#bfdbfe' },
            { title: 'Active Leads', value: stats?.total_leads || 0, icon: Target, accent: '#d97706', bg: '#fffbeb', border: '#fde68a' },
            { title: 'Properties', value: stats?.total_properties || 0, icon: Home, accent: '#7c3aed', bg: '#f5f3ff', border: '#c4b5fd' },
          ].map((stat, i) => (
            <div key={i} className="group relative overflow-hidden rounded-xl border p-3 md:p-4 transition-all duration-200 hover:shadow-lg hover:-translate-y-0.5 cursor-pointer" style={{ background: stat.bg, borderColor: stat.border }} data-testid={`stat-${stat.title.toLowerCase().replace(/ /g,'-')}`}>
              <div className="flex items-start justify-between gap-1">
                <div className="min-w-0 flex-1">
                  <p className="text-[10px] md:text-xs font-semibold tracking-wide uppercase text-slate-500 truncate">{stat.title}</p>
                  <p className="text-base sm:text-lg md:text-2xl lg:text-3xl font-bold mt-1 truncate" style={{ color: BRAND.navy }}>{stat.value}</p>
                </div>
                <div className="p-1.5 md:p-2.5 rounded-lg md:rounded-xl shadow-sm shrink-0" style={{ background: stat.accent }}>
                  <stat.icon size={16} className="text-white md:hidden" />
                  <stat.icon size={20} className="text-white hidden md:block" />
                </div>
              </div>
              <div className="absolute bottom-0 left-0 right-0 h-1 opacity-0 group-hover:opacity-100 transition-opacity" style={{ background: stat.accent }}></div>
            </div>
          ))}
        </div>

        {/* ===== FINANCIAL OVERVIEW ROW ===== */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
          <div className="flex items-center gap-2 md:gap-3 p-3 md:p-4 rounded-xl border border-emerald-200 bg-emerald-50/50" data-testid="this-month-paid">
            <div className="p-1.5 md:p-2 rounded-lg bg-emerald-500 shrink-0"><CheckCircle size={16} className="text-white" /></div>
            <div className="min-w-0">
              <p className="text-[10px] md:text-xs text-slate-500 font-medium truncate">This Month Collected</p>
              <p className="text-sm md:text-lg font-bold text-emerald-700 truncate">{formatCurrency(stats?.current_month?.paid || 0)}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 md:gap-3 p-3 md:p-4 rounded-xl border border-amber-200 bg-amber-50/50" data-testid="pending-amount">
            <div className="p-1.5 md:p-2 rounded-lg bg-amber-500 shrink-0"><Clock size={16} className="text-white" /></div>
            <div className="min-w-0">
              <p className="text-[10px] md:text-xs text-slate-500 font-medium truncate">Pending Amount</p>
              <p className="text-sm md:text-lg font-bold text-amber-700 truncate">{formatCurrency(stats?.pending_amount || 0)}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 md:gap-3 p-3 md:p-4 rounded-xl border border-blue-200 bg-blue-50/50" data-testid="month-bookings">
            <div className="p-1.5 md:p-2 rounded-lg bg-blue-500 shrink-0"><Calendar size={16} className="text-white" /></div>
            <div className="min-w-0">
              <p className="text-[10px] md:text-xs text-slate-500 font-medium truncate">This Month Bookings</p>
              <p className="text-sm md:text-lg font-bold text-blue-700">{stats?.current_month?.bookings || 0}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 md:gap-3 p-3 md:p-4 rounded-xl border border-slate-200 bg-slate-50/50" data-testid="collection-rate">
            <div className="p-1.5 md:p-2 rounded-lg shrink-0" style={{ background: BRAND.navy }}><BarChart3 size={16} style={{ color: BRAND.gold }} /></div>
            <div className="min-w-0">
              <p className="text-[10px] md:text-xs text-slate-500 font-medium">Collection Rate</p>
              <div className="flex items-center gap-2">
                <p className="text-sm md:text-lg font-bold" style={{ color: BRAND.navy }}>{collectionRate}%</p>
                <div className="flex-1 h-2 bg-slate-200 rounded-full w-12 md:w-16 overflow-hidden">
                  <div className="h-full rounded-full transition-all duration-700" style={{ width: `${collectionRate}%`, background: collectionRate >= 60 ? '#059669' : collectionRate >= 40 ? '#d97706' : '#ef4444' }}></div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ===== MAIN CONTENT GRID ===== */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-5">

          {/* Revenue Chart */}
          <Card className="lg:col-span-2 border-0 shadow-md overflow-hidden">
            <CardHeader className="pb-1 border-b bg-slate-50/50">
              <CardTitle className="text-sm flex items-center gap-2 font-semibold text-slate-700">
                <TrendingUp size={16} className="text-emerald-600" />
                Revenue Trend
                <span className="text-xs font-normal text-slate-400 ml-auto">Monthly Revenue</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-4">
              <div className="h-56">
                {revenueData.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={revenueData}>
                      <defs>
                        <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor={BRAND.navy} stopOpacity={0.15} />
                          <stop offset="100%" stopColor={BRAND.navy} stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                      <XAxis dataKey="month" stroke="#94a3b8" fontSize={11} tickLine={false} />
                      <YAxis stroke="#94a3b8" fontSize={11} tickLine={false} axisLine={false} tickFormatter={v => `₹${(v/100000).toFixed(0)}L`} />
                      <Tooltip contentStyle={{ borderRadius: 10, border: '1px solid #e2e8f0', boxShadow: '0 8px 30px rgba(0,0,0,0.08)', fontSize: 13 }} formatter={v => [`₹${Number(v).toLocaleString('en-IN')}`, 'Revenue']} />
                      <Area type="monotone" dataKey="revenue" stroke={BRAND.navy} strokeWidth={2.5} fill="url(#revGrad)" dot={{ r: 3, fill: BRAND.navy }} activeDot={{ r: 5, fill: BRAND.gold, stroke: BRAND.navy, strokeWidth: 2 }} />
                    </AreaChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-full flex items-center justify-center text-slate-400 text-sm">Chart data loading...</div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Right Column */}
          <div className="space-y-4">
            {/* AI Insight */}
            <div className="rounded-xl overflow-hidden" style={{ background: `linear-gradient(135deg, ${BRAND.navy}, #1a2840)` }}>
              <div className="p-4">
                <div className="flex items-center gap-2 mb-3">
                  <div className="p-1.5 rounded-lg" style={{ background: `${BRAND.gold}30` }}>
                    <Sparkles size={14} style={{ color: BRAND.gold }} />
                  </div>
                  <span className="text-xs font-semibold" style={{ color: BRAND.gold }}>AI INSIGHT</span>
                </div>
                {loadingInsight ? (
                  <div className="flex items-center gap-2 text-slate-400 text-sm"><div className="w-3 h-3 border-2 border-slate-500 border-t-white rounded-full animate-spin"></div>Analyzing...</div>
                ) : (
                  <p className="text-slate-300 text-sm leading-relaxed">{aiInsight}</p>
                )}
              </div>
            </div>

            {/* Quick Actions */}
            <Card className="border-0 shadow-md">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold text-slate-700">Quick Actions</CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-2 gap-2">
                {[
                  { label: 'New Lead', icon: Plus, path: '/admin/leads', color: '#059669' },
                  { label: 'Billing', icon: CreditCard, path: '/admin/billing', color: '#2563eb' },
                  { label: 'Customers', icon: Users, path: '/admin/customers', color: '#7c3aed' },
                  { label: 'Properties', icon: Building2, path: '/admin/properties', color: '#d97706' },
                ].map((action, i) => (
                  <button key={i} onClick={() => navigate(action.path)} className="flex items-center gap-2 p-2.5 rounded-lg border border-slate-100 hover:border-slate-200 hover:bg-slate-50 transition-all text-left group" data-testid={`quick-${action.label.toLowerCase().replace(/ /g,'-')}`}>
                    <div className="p-1.5 rounded-md" style={{ background: `${action.color}15` }}>
                      <action.icon size={14} style={{ color: action.color }} />
                    </div>
                    <span className="text-xs font-medium text-slate-600 group-hover:text-slate-800">{action.label}</span>
                  </button>
                ))}
              </CardContent>
            </Card>

            {/* Daily Quote */}
            <div className="rounded-xl p-4 border-l-3" style={{ background: `${BRAND.gold}10`, borderLeft: `3px solid ${BRAND.gold}` }}>
              <div className="flex items-center gap-1.5 mb-2">
                <Quote size={13} style={{ color: BRAND.gold }} />
                <span className="text-xs font-semibold" style={{ color: BRAND.gold }}>आज का विचार</span>
              </div>
              <p className="text-sm leading-relaxed italic" style={{ color: BRAND.navy }}>"{dailyQuote}"</p>
            </div>
          </div>
        </div>

        {/* ===== FOLLOW-UP REMINDERS + RECENT ACTIVITY ===== */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">

          {/* Today's Follow-ups */}
          <Card className="border-0 shadow-md">
            <CardHeader className="pb-2 border-b bg-slate-50/50">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm flex items-center gap-2 font-semibold text-slate-700">
                  <CalendarClock size={16} className="text-amber-500" />
                  Follow-up Reminders
                  {followupLeads.length > 0 && <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full font-bold">{followupLeads.length}</span>}
                </CardTitle>
                <Button variant="ghost" size="sm" onClick={() => navigate('/admin/leads')} className="text-xs text-slate-500 gap-1 h-7"><Eye size={12} /> View All</Button>
              </div>
            </CardHeader>
            <CardContent className="pt-3">
              {followupLeads.length === 0 ? (
                <div className="text-center py-6 text-slate-400 text-sm">
                  <CheckCircle size={28} className="mx-auto mb-2 text-emerald-300" />
                  Koi pending follow-up nahi hai!
                </div>
              ) : (
                <div className="space-y-2 max-h-52 overflow-y-auto">
                  {followupLeads.slice(0, 6).map((lead, i) => {
                    const overdue = isOverdue(lead.next_followup_date);
                    return (
                      <div key={lead.id || i} className={`flex items-center justify-between p-2.5 rounded-lg border ${overdue ? 'bg-red-50/60 border-red-200' : 'bg-amber-50/60 border-amber-200'}`} data-testid={`dashboard-followup-${i}`}>
                        <div className="flex-1 min-w-0 mr-2">
                          <p className="text-sm font-semibold text-slate-800 truncate">{lead.name}</p>
                          <div className="flex items-center gap-2 text-xs text-slate-500">
                            <Phone size={10} /> {lead.phone}
                            <span className={`font-medium ${overdue ? 'text-red-600' : 'text-amber-600'}`}>
                              {overdue ? <><AlertTriangle size={10} className="inline" /> Overdue</> : 'Today'}
                            </span>
                          </div>
                          {lead.next_followup_note && <p className="text-xs text-slate-400 truncate mt-0.5">{lead.next_followup_note}</p>}
                        </div>
                        <Button size="sm" onClick={() => navigate('/admin/leads')} className={`h-7 px-2 text-xs gap-1 ${overdue ? 'bg-red-600 hover:bg-red-700' : 'bg-amber-600 hover:bg-amber-700'} text-white`}>
                          <Phone size={11} /> Call
                        </Button>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Recent Activity */}
          <Card className="border-0 shadow-md">
            <CardHeader className="pb-2 border-b bg-slate-50/50">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm flex items-center gap-2 font-semibold text-slate-700">
                  <Activity size={16} className="text-blue-500" />
                  Recent Bookings
                </CardTitle>
                <Button variant="ghost" size="sm" onClick={() => navigate('/admin/billing')} className="text-xs text-slate-500 gap-1 h-7"><Eye size={12} /> View All</Button>
              </div>
            </CardHeader>
            <CardContent className="pt-3">
              {recentBookings.length === 0 ? (
                <div className="text-center py-6 text-slate-400 text-sm">
                  <FileText size={28} className="mx-auto mb-2 text-slate-300" />
                  Loading recent activity...
                </div>
              ) : (
                <div className="space-y-2 max-h-52 overflow-y-auto">
                  {recentBookings.map((b, i) => (
                    <div key={b.id || i} className="flex items-center justify-between p-2.5 rounded-lg bg-slate-50/80 border border-slate-100 hover:bg-slate-100/80 transition-colors" data-testid={`recent-booking-${i}`}>
                      <div className="flex-1 min-w-0 mr-2">
                        <p className="text-sm font-semibold text-slate-800 truncate">{b.customer_name || 'Customer'}</p>
                        <p className="text-xs text-slate-500">{b.property_name || 'Property'} {b.booking_number && `#${b.booking_number}`}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-bold text-slate-700">{formatCurrency(b.total_price || 0)}</p>
                        <span className={`text-xs font-semibold px-1.5 py-0.5 rounded ${(b.payment_status || '').includes('Paid') && !(b.payment_status || '').includes('Partial') ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                          {b.payment_status || 'Pending'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
}
